/**
 * Research row side panel (2026-09-11 research-verdict-simplification
 * design, Faraz: "full detail lives in a side panel, list stays put"). Opens
 * on a ResearchPage row click; everything the old inline table columns used
 * to show (tier, listing, pack, viable-at, offers, rank, gating, identity,
 * velocity, price context, amazon-retail) lives here instead, grouped into
 * Verdict / Money / Listing / Demand / Pack sections in the design doc's
 * order. No Prep section: PlanningResearchRow carries no seller SKU (that
 * only exists once a candidate becomes a live Amazon listing), so there is
 * nothing to hang a glass-wrap toggle on for a row still at the research
 * stage — see the implementation report for this omission.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import type { PlanningResearchRow } from '@sourcito/schemas';
import { ProductLink } from '../../components/ProductLink';
import { Badge, Button, Input } from '../../design';
import { useMe } from '../../lib/auth';
import {
  amazonRetailChip,
  discountViableLabel,
  discountViableTitle,
  gateKindLabel,
  gatingChip,
  gatingReasonsTitle,
  identityMismatchTitle,
  money,
  packBadge,
  parseFeeOverrideInput,
  percent,
  researchAnchorLine,
  researchStatus,
  sellableOverrideChip,
  useDeleteFeeOverride,
  useDeletePackOverride,
  useDeleteSellableOverride,
  usePutFeeOverride,
  usePutPackOverride,
  usePutSellableOverride,
  velocityChip,
  velocitySourceLabel,
  verdictBucketLabel,
  verdictReasonWords,
  verdictTierBadge,
} from '../../lib/order-planning';

type BadgeVariant = 'neutral' | 'accent' | 'positive' | 'warning' | 'negative' | 'info';

const BUCKET_BADGE_VARIANT: Record<PlanningResearchRow['verdict']['bucket'], BadgeVariant> = {
  buy: 'positive',
  fix: 'warning',
  no: 'negative',
};

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="py-4 border-b border-border-subtle last:border-b-0">
      <h3 className="text-xs uppercase tracking-[0.14em] text-fg-tertiary mb-2">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-fg-tertiary shrink-0">{label}</span>
      <span className="text-right text-fg-primary min-w-0">{children}</span>
    </div>
  );
}

/** A missing pricing verdict is incomplete evidence, not a no-listing decision. */
export function researchDecisionReason(row: PlanningResearchRow): string {
  if (row.tier !== null || row.verdict.reason === 'incomplete_economics') {
    return verdictReasonWords(row.verdict.reason);
  }
  const reason = row.reason?.replaceAll('_', ' ').trim();
  return reason ? reason.charAt(0).toUpperCase() + reason.slice(1) : 'Research incomplete';
}

export function ResearchRowPanel({
  row,
  onClose,
}: {
  row: PlanningResearchRow;
  onClose: () => void;
}) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, [row.candidateId]);

  useEffect(() => {
    // Node-environment component tests (react-test-renderer, no jsdom) have
    // no `document` global at all — guard rather than crash; the browser
    // always has one.
    if (typeof document === 'undefined') return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const label = row.description ?? row.itemNo ?? row.upc ?? row.key;
  const tierBadge = verdictTierBadge(row.tier ?? 'unknown');
  const velocity = velocityChip(row.velocity.tier);
  const gating = gatingChip(row.gating.status);
  const gateKind = gateKindLabel(row.gating.gateKind);
  const showApplyLink = row.gating.status === 'approval_required' && row.gating.applyUrl !== null;
  const amazonChip = amazonRetailChip(row.amazonRetail);
  const pack = packBadge(row.pack);
  const identity = row.amazon.identity;
  const unitsPerListing = row.pack.vendorUnitsPerListing ?? row.unitsPerListing;
  const priceSpiked = row.flags.includes('price_spike');
  const status = researchStatus(row);
  // Manual sellable override (2026-09-17 design) — see sellableOverrideChip's
  // doc. Set/clear is owner/operator only; the chip itself (once set) is
  // informational and shown to every role.
  const sellable = sellableOverrideChip(row.sellableOverride);
  const { data: me } = useMe();
  const canEditSellable = me?.user.role === 'owner' || me?.user.role === 'operator';

  return (
    <>
      {/* Backdrop — a separate full-screen element behind the panel, so a
       *  click anywhere the panel doesn't cover closes it without needing
       *  stopPropagation on the panel's own content. */}
      <div className="fixed inset-0 z-40 bg-overlay/60" onClick={onClose} aria-hidden="true" />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Research detail: ${label}`}
        className="fixed inset-y-0 right-0 z-50 w-full md:w-[420px] bg-surface border-l border-border-subtle overflow-y-auto p-6 shadow-lg"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-fg-primary font-medium line-clamp-2">{label}</p>
            {row.amazon.title && (
              <p className="text-xs text-fg-tertiary line-clamp-1 mt-0.5">
                <ProductLink asin={row.asin} className="hover:underline">
                  {row.amazon.title}
                </ProductLink>
              </p>
            )}
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="shrink-0 text-fg-tertiary hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 rounded-xs"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* 2026-09-17 provider-queues Task 6: same per-provider evidence
         *  badges as the table row (researchStatus) — Keepa is enrichment,
         *  so an Amazon-only row reaching this panel is expected, not a
         *  bug; this line says so plainly instead of leaving blank
         *  priceContext/velocity fields to explain themselves. */}
        <p className="mt-3 text-[10px] text-fg-tertiary" aria-label="Panel research status">
          Amazon {status.amazon} · Keepa {status.keepa}
        </p>

        <Section title="Verdict">
          <div className="flex items-center gap-2 mb-2">
            <Badge
              variant={row.tier === null ? 'warning' : BUCKET_BADGE_VARIANT[row.verdict.bucket]}
            >
              {row.tier === null ? 'Incomplete' : verdictBucketLabel(row.verdict.bucket)}
            </Badge>
            <p className="text-sm text-fg-primary">{researchDecisionReason(row)}</p>
          </div>

          <Line label="Margin tier">
            <Badge variant={tierBadge.variant}>{tierBadge.label}</Badge>
          </Line>

          <Line label="Sales/mo">
            <span className="inline-flex items-center gap-1.5">
              {row.velocity.estimate !== null ? row.velocity.estimate.toLocaleString('en-US') : '—'}
              <Badge variant={velocity.variant}>{velocity.label}</Badge>
            </span>
          </Line>

          <Line label="Amazon">
            {amazonChip ? (
              <Badge variant={amazonChip.variant} title={amazonChip.title}>
                {amazonChip.label}
              </Badge>
            ) : row.amazonRetail.status === 'clear' ? (
              <Badge variant="positive">Clear</Badge>
            ) : (
              <Badge variant="neutral">Unknown</Badge>
            )}
          </Line>

          {sellable && (
            <Line label="Listing">
              <Badge variant="positive" title={sellable.title}>
                {sellable.label}
              </Badge>
            </Line>
          )}

          <Line label={sellable ? "Amazon's answer" : 'Listing gate'}>
            <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
              <Badge
                variant={gating.variant}
                title={gatingReasonsTitle(row.gating.reasons, row.gating.messages)}
              >
                {gating.label}
              </Badge>
              {gateKind && <span className="text-[10px] text-fg-tertiary">{gateKind}</span>}
              {showApplyLink && (
                <a
                  href={row.gating.applyUrl!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-fg-secondary underline underline-offset-2 hover:text-fg-primary"
                >
                  Apply
                </a>
              )}
            </span>
          </Line>

          {canEditSellable && row.asin && (
            <div className="flex justify-end">
              <SellableOverrideControl asin={row.asin} sellable={sellable} />
            </div>
          )}

          <Line label="Listing match">
            {identity === 'mismatch' ? (
              <Badge variant="negative" title={identityMismatchTitle(row.amazon.identityReason)}>
                Wrong product?
              </Badge>
            ) : identity === 'match' ? (
              <Badge variant="positive">Match</Badge>
            ) : (
              <Badge variant="neutral">Unverified</Badge>
            )}
          </Line>

          <Line label="Pack">
            {pack ? (
              <Badge variant={pack.variant} title={pack.title}>
                {pack.label}
              </Badge>
            ) : row.pack.suspect ? (
              <Badge
                variant="warning"
                title="Price looks off for the current pack — enter the correct units per listing"
              >
                Verify pack
              </Badge>
            ) : (
              <span className="text-fg-secondary">Single unit (assumed)</span>
            )}
          </Line>

          <Line label="Price vs 90d avg">
            {row.priceContext.bbAvg90Cents !== null ? (
              <span className="inline-flex items-center gap-1.5">
                {money(row.priceContext.bbAvg90Cents)}
                {priceSpiked && row.priceContext.anchorVsAvg90Pct !== null && (
                  <Badge variant="warning">
                    {`+${Math.round(row.priceContext.anchorVsAvg90Pct)}%`}
                  </Badge>
                )}
              </span>
            ) : (
              '—'
            )}
          </Line>

          <Line label="Anchor">{researchAnchorLine(row)}</Line>
        </Section>

        <Section title="Money">
          {row.researchNote && <p className="text-sm text-fg-secondary">{row.researchNote}</p>}
          <Line label="Cost">
            <span>
              {money(row.costCents)}
              {unitsPerListing !== null && unitsPerListing > 1 && row.costCents !== null && (
                <span className="block text-[10px] text-fg-tertiary">
                  {`${money(Math.round(row.costCents / unitsPerListing))} / unit × ${unitsPerListing}`}
                </span>
              )}
            </span>
          </Line>
          <Line label="Fees">{money(row.estFeesCents)}</Line>
          <Line label="Margin">
            <span>
              {money(row.estMarginCents)}{' '}
              <span className="text-fg-tertiary">({percent(row.marginPct)})</span>
            </span>
          </Line>
          <Line label="Buyable at">
            <span title={discountViableTitle(row.discount)}>
              {discountViableLabel(row.discount)}
            </span>
          </Line>
        </Section>

        <Section title="Listing">
          <Line label="UPC">{row.upc || '—'}</Line>
          <Line label="ASIN">
            <ProductLink asin={row.asin}>{row.asin ?? '—'}</ProductLink>
          </Line>
          <Line label="Brand">{row.amazon.brand ?? '—'}</Line>
          <Line label="Category">{row.amazon.displayGroup ?? row.category ?? '—'}</Line>
          <Line label="Gate kind">{gateKind ?? '—'}</Line>
        </Section>

        <Section title="Demand">
          <Line label="Monthly sold">
            {row.velocity.estimate !== null ? row.velocity.estimate.toLocaleString('en-US') : '—'}
          </Line>
          <Line label="Source">{velocitySourceLabel(row.velocity.source)}</Line>
          <Line label="Sales rank">
            {row.salesRank !== null ? row.salesRank.toLocaleString('en-US') : '—'}
          </Line>
          <Line label="90d avg price">{money(row.priceContext.bbAvg90Cents)}</Line>
          <Line label="30d avg price">{money(row.priceContext.bbAvg30Cents)}</Line>
          <Line label="Offers">{row.offerCount ?? '—'}</Line>
        </Section>

        <Section title="Pack">
          <PackPanelSection row={row} />
        </Section>

        <Section title="Verified fee">
          <FeeOverridePanelSection row={row} canEdit={canEditSellable} />
        </Section>
      </aside>
    </>
  );
}

/** Manual sellable override control (2026-09-17 design, Faraz: "I need a
 *  way to mark a product sellable in planning or research page. Once I
 *  check manually, I should be able to remove the restrictions.") — sits
 *  right under the Listing-gate line, next to the existing approval link.
 *  Save writes PUT /api/order-planning/sellable-overrides/:asin, Clear
 *  writes DELETE, same "Saved" confirmation convention as
 *  PackPanelSection below. Owner/operator gating (canEditSellable) is
 *  enforced by the caller — this component assumes it may render. */
function SellableOverrideControl({
  asin,
  sellable,
}: {
  asin: string;
  sellable: { label: string; title: string | undefined } | null;
}) {
  const putOverride = usePutSellableOverride();
  const deleteOverride = useDeleteSellableOverride();
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState('');
  const [justSaved, setJustSaved] = useState(false);

  function save() {
    const trimmed = note.trim();
    putOverride.mutate(
      { asin, note: trimmed === '' ? undefined : trimmed },
      {
        onSuccess: () => {
          setJustSaved(true);
          setEditing(false);
        },
      }
    );
  }

  function clear() {
    setJustSaved(false);
    deleteOverride.mutate({ asin }, { onSuccess: () => setJustSaved(true) });
  }

  const errorMessage =
    (putOverride.error instanceof Error ? putOverride.error.message : null) ??
    (deleteOverride.error instanceof Error ? deleteOverride.error.message : null);

  return (
    <div className="space-y-1.5 text-right">
      {sellable ? (
        <Button size="sm" variant="ghost" disabled={deleteOverride.isPending} onClick={clear}>
          Clear
        </Button>
      ) : editing ? (
        <div className="space-y-1.5">
          <Input
            aria-label="Sellable override note"
            inputSize="sm"
            placeholder="Optional note"
            disabled={putOverride.isPending}
            value={note}
            onChange={(e) => {
              setJustSaved(false);
              setNote(e.target.value);
            }}
          />
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="secondary" disabled={putOverride.isPending} onClick={save}>
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
          Mark sellable
        </Button>
      )}
      {(putOverride.isPending || deleteOverride.isPending) && (
        <p className="text-[10px] text-fg-tertiary">Saving…</p>
      )}
      {!putOverride.isPending && !deleteOverride.isPending && justSaved && (
        <p className="text-[10px] text-fg-tertiary">Saved</p>
      )}
      {errorMessage && <p className="text-[10px] text-negative">{errorMessage}</p>}
    </div>
  );
}

/** Single-input pack override editor — Save writes PUT
 *  /api/order-planning/pack-overrides/:asin, Clear writes DELETE, both via
 *  the same mutation hooks the old inline table cell used. Simpler than the
 *  table's click-to-edit dance (no read-only/pencil affordance): the panel
 *  is already a deliberate "look at one product" surface, so the field is
 *  always editable. */
function PackPanelSection({ row }: { row: PlanningResearchRow }) {
  const putOverride = usePutPackOverride();
  const deleteOverride = useDeletePackOverride();
  const current = row.pack.override ?? row.pack.vendorUnitsPerListing;
  const [value, setValue] = useState(current !== null ? String(current) : '');
  // M6 code-review catch (2026-09-16): "Saved" must show after EITHER a
  // successful PUT or a successful DELETE (Clear) — not just PUT, and not
  // gated on row.pack.override's own (possibly-stale, pre-refetch) value.
  const [justSaved, setJustSaved] = useState(false);

  if (!row.asin) {
    return (
      <p className="text-sm text-fg-secondary">
        No ASIN on this row yet — pack quantity can be set once one resolves.
      </p>
    );
  }

  function save() {
    const n = Number(value);
    if (value.trim() === '' || !Number.isInteger(n) || n < 1 || n > 999) return;
    putOverride.mutate(
      { asin: row.asin!, vendorUnitsPerListing: n },
      { onSuccess: () => setJustSaved(true) }
    );
  }

  function clear() {
    setJustSaved(false);
    deleteOverride.mutate({ asin: row.asin! }, { onSuccess: () => setJustSaved(true) });
  }

  const errorMessage = putOverride.error instanceof Error ? putOverride.error.message : null;

  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm">
        <span className="text-fg-tertiary">Units per listing</span>
        <Input
          aria-label="Units per listing"
          inputSize="sm"
          type="number"
          disabled={putOverride.isPending}
          value={value}
          onChange={(e) => {
            setJustSaved(false);
            setValue(e.target.value);
          }}
          className="w-20"
        />
      </label>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="secondary" disabled={putOverride.isPending} onClick={save}>
          Save
        </Button>
        {row.pack.override !== null && (
          <Button size="sm" variant="ghost" disabled={deleteOverride.isPending} onClick={clear}>
            Clear
          </Button>
        )}
      </div>
      {(putOverride.isPending || deleteOverride.isPending) && (
        <p className="text-[10px] text-fg-tertiary">Saving…</p>
      )}
      {/* Pack-quantity design (2026-09-16): the override is applied
          server-side immediately (Planning read, this projection, "check
          margin again") — no more "applies next run" hint. Shows after
          EITHER a successful save or clear (M6). */}
      {!putOverride.isPending && !deleteOverride.isPending && justSaved && (
        <p className="text-[10px] text-fg-tertiary">Saved</p>
      )}
      {errorMessage && <p className="text-[10px] text-negative">{errorMessage}</p>}
    </div>
  );
}

/** Verified fee box (2026-09-30 design) — for a product with an Amazon price
 *  but no Amazon fee quote. The VA reads the fees in Amazon's Revenue
 *  Calculator and types them here; a verified fee gives a full verdict, and a
 *  real Amazon fee quote still wins over it. Same Save/Clear layout as
 *  PackPanelSection. Owner/operator only, same as the API. */
function FeeOverridePanelSection({ row, canEdit }: { row: PlanningResearchRow; canEdit: boolean }) {
  const putFee = usePutFeeOverride();
  const deleteFee = useDeleteFeeOverride();
  const saved = row.feeOverride;
  const referralFraction = saved?.referralFeePct ?? row.feesReferralPct;
  const [fee, setFee] = useState(saved ? (saved.fulfillmentFeeCents / 100).toFixed(2) : '');
  // Fraction -> percent without float noise (0.15 * 100 = 15.000000000000002).
  const [referral, setReferral] = useState(
    referralFraction !== null ? String(Math.round(referralFraction * 10000) / 100) : ''
  );
  const [justSaved, setJustSaved] = useState(false);
  const [invalid, setInvalid] = useState(false);

  if (!row.asin) {
    return (
      <p className="text-sm text-fg-secondary">
        No ASIN on this row yet — a verified fee can be set once one resolves.
      </p>
    );
  }

  function save() {
    const parsed = parseFeeOverrideInput(fee, referral);
    if (!parsed.ok) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    putFee.mutate(
      {
        asin: row.asin!,
        fulfillmentFeeCents: parsed.fulfillmentFeeCents,
        referralFeePct: parsed.referralFeePct,
      },
      { onSuccess: () => setJustSaved(true) }
    );
  }

  function clear() {
    setJustSaved(false);
    setInvalid(false);
    deleteFee.mutate({ asin: row.asin! }, { onSuccess: () => setJustSaved(true) });
  }

  const busy = putFee.isPending || deleteFee.isPending;
  const errorMessage = putFee.error instanceof Error ? putFee.error.message : null;

  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm">
        <span className="text-fg-tertiary">FBA fee ($)</span>
        <Input
          aria-label="FBA fee ($)"
          inputSize="sm"
          type="number"
          disabled={!canEdit || putFee.isPending}
          value={fee}
          onChange={(e) => {
            setJustSaved(false);
            setInvalid(false);
            setFee(e.target.value);
          }}
          className="w-24"
        />
      </label>
      <label className="flex items-center gap-2 text-sm">
        <span className="text-fg-tertiary">Referral (%)</span>
        <Input
          aria-label="Referral (%)"
          inputSize="sm"
          type="number"
          disabled={!canEdit || putFee.isPending}
          value={referral}
          onChange={(e) => {
            setJustSaved(false);
            setInvalid(false);
            setReferral(e.target.value);
          }}
          className="w-24"
        />
      </label>
      <p className="text-[10px] text-fg-tertiary">From Amazon's Revenue Calculator</p>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          aria-label="Save verified fee"
          disabled={!canEdit || putFee.isPending}
          onClick={save}
        >
          Save
        </Button>
        {saved !== null && (
          <Button
            size="sm"
            variant="ghost"
            aria-label="Clear verified fee"
            disabled={!canEdit || deleteFee.isPending}
            onClick={clear}
          >
            Clear
          </Button>
        )}
      </div>
      {invalid && (
        <p className="text-[10px] text-negative">
          Enter a fee above $0 and a referral from 0 to 50%.
        </p>
      )}
      {busy && <p className="text-[10px] text-fg-tertiary">Saving…</p>}
      {!busy && justSaved && <p className="text-[10px] text-fg-tertiary">Saved</p>}
      {errorMessage && <p className="text-[10px] text-negative">{errorMessage}</p>}
    </div>
  );
}
